'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { GetServerSideProps } from 'next';
import { useRouter, useSearchParams } from 'next/navigation';
import { history } from 'instantsearch.js/es/lib/routers';
import { renderToString } from 'react-dom/server';
import { InstantSearch, Configure, InstantSearchSSRProvider, getServerState } from 'react-instantsearch';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import ProductListingPage from '@/components/customercenter-ui/components/product/search';
import { ProductListingProvider } from '@/components/customercenter-ui/components/product/search/components/context/productListingContext';
import { useProductListingContext } from '@/components/customercenter-ui/components/product/search/components/hook/productList';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { createAlgoliaLiteClient, wrapInstantSearchClient } from '@/helpers/algolia/algoliaV5';
import { getSelectedDivision } from '@/helpers/utils/siteUtils';
import CustomStateMapping from './customRouteMapping';

function ProductListingPageTastic({ data }: any) {
  const searchParams = useSearchParams();
  const searchQuery = searchParams.get('q') as string;
  const categorySlug = '';
  const { contextValue } = useProductListingContext(searchQuery, data, categorySlug);

  return (
    <ProductListingProvider value={contextValue}>
      <ProductListingPage />
    </ProductListingProvider>
  );
}

const SearchPageTastic = ({ data, serverState, ...props }: any) => {
  const projectSettings: ProjectSettings = useProjSettings();
  const virtualAlgoliaConfig = projectSettings?.isVirtualAlgoliaCatelogSearchEnabled
    ? JSON.parse(projectSettings?.isVirtualAlgoliaCatelogSearchEnabled)
    : {};
  const [isClient, setIsClient] = useState(false);
  const [algoliaUIReady, setAlgoliaReady] = useState(false);
  const [customFilter, setCustomFilter] = useState(null) as any;
  const [facet, setFacet] = useState(null) as any;
  const [siteId, setSiteId] = useState('ZBSite');
  const searchParams = useSearchParams();
  const modifiedData = { ...data };
  modifiedData.siteId = siteId;
  modifiedData.facetNames = modifiedData.facetNames.replace(/"refinementColor"/g, '"colorRefinement"');
  const searchClient = wrapInstantSearchClient(
    createAlgoliaLiteClient(projectSettings?.algoliaAppId, projectSettings?.algoliaApiKey),
  );
  const router = useRouter();
  const searchQuery = searchParams.get('q') || '';

  const index = useMemo(() => {
    const isVirtualAlgoliaEnabledCatelogSearchSite = virtualAlgoliaConfig?.[siteId];
    if (isVirtualAlgoliaEnabledCatelogSearchSite === true || isVirtualAlgoliaEnabledCatelogSearchSite === 'true') {
      return `products_${siteId}_CSA`;
    } else {
      return `products_${siteId}_search`;
    }
  }, [siteId]);

  useEffect(() => {
    const selectedDivision = getSelectedDivision() ?? 'ZB';
    setSiteId(`${selectedDivision}Site`);
  }, []);

  const routing = {
    stateMapping: CustomStateMapping(index),
    router: history(),
  } as any;

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    const facet: any = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('facet') : null;
    setFacet(facet);
    if (facet) {
      sessionStorage?.setItem('pageLoadFacets', facet);
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem('isBackButtonCalled', '0');
    const url = new URL(window.location.href);
    const facet = url.searchParams.get('facet');
    if (!facet) {
      url.searchParams.delete('facet');
      router.replace(url?.toString());
    }
    if (sessionStorage.getItem('removeFacetOnReturn') === '1') {
      sessionStorage.removeItem('removeFacetOnReturn');
    }
    sessionStorage.setItem('currentPage', 'search');
    window.scroll(0, 0);
  }, []);

  const configureProps = useMemo(() => {
    return {
      query: searchQuery,
      hitsPerPage: 6,
      clickAnalytics: false,
      analytics: false,
      maxValuesPerFacet: 1000,
      facets: ['*'],
      page: 0,
    };
  }, [searchQuery]);
  const searchIndexKey = useMemo(() => `${index}-${searchQuery}`, [index, searchQuery]);

  return (
    <>
      {isClient && (
        <InstantSearchSSRProvider {...serverState}>
          <InstantSearch
            searchClient={searchClient}
            indexName={index}
            routing={routing as any}
            future={{ preserveSharedStateOnUnmount: true }}
            key={searchIndexKey}
          >
            <Configure {...(configureProps as any)} />
            <ProductListingPageTastic
              data={{ ...modifiedData }}
              algoliaUIReady={algoliaUIReady}
              algoliaProductHitsPerPage={6}
              setCustomFilter={setCustomFilter}
              schemaOverride={props?.PLPSeoSchema}
              props={props}
            />
          </InstantSearch>
        </InstantSearchSSRProvider>
      )}
    </>
  );
};

export const getServerSideProps: GetServerSideProps<any> = async ({ req }) => {
  const protocol = req.headers.referer?.split('://')[0] || 'https';
  const url = `${protocol}://${req.headers.host}${req.url}`;
  const serverState = await getServerState(<SearchPageTastic url={url} />, {
    renderToString,
  });
  return {
    props: {
      serverState,
      url,
    },
  };
};

export default SearchPageTastic;
